import React, { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';

import { Nullable, ResourceType } from '@tager/web-core';

import { getAliasString } from '@/utils/common';
import { useTypedSelector } from '@/store/store';
import { selectProductResource } from '@/store/reducers/pages/product/product';
import ContentContainer from '@/components/ContentContainer';
import { colors } from '@/constants/theme';
import { ReactComponent as Arrow } from '@/assets/svg/arrow_question.svg';
import { FullProductType } from '@/typings/model';

type Props = {
  product: Nullable<FullProductType>;
};

function Related({ product }: Props) {
  const [isOptionsOpen, setOpen] = useState(false);
  const [currentIndex, setIndex] = useState(0);

  const router = useRouter();

  const relatedList = useMemo(() => {
    if (!product?.related?.length) {
      return null;
    }

    return product.related;
  }, [product?.related]);

  return (
    <Component>
      {!!relatedList?.length ? (
        <Content>
          {relatedList.map((item, index) => (
            <>
              <Label>{item.name}</Label>
              <SelectWrapper>
                <StyledArrow isopen={isOptionsOpen} />
                <Select
                  className="multiprice-select"
                  onFocus={() => {
                    setOpen((pre) => !pre);
                  }}
                  onBlur={(e) => {
                    e.target.click();
                    setOpen(false);
                  }}
                  onChange={(e) => {
                    const index = e.target.selectedIndex;
                    const alias = item.options[index].productAlias;
                    if (index !== currentIndex) {
                      setIndex(index);
                      if (alias) {
                        router.push('/products/' + alias);
                      }
                      setOpen((pre) => !pre);
                    }
                  }}
                >
                  {item.options.map((option, index) => (
                    <Option
                      selected={index === currentIndex}
                      isSelected={index === currentIndex}
                      key={index}
                    >
                      {option.name}
                    </Option>
                  ))}
                </Select>
              </SelectWrapper>
            </>
          ))}
        </Content>
      ) : null}
    </Component>
  );
}

export default Related;

const Component = styled.div`
  margin-top: 17px;
`;

const Label = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;
`;

const SelectWrapper = styled.div`
  margin-top: 7px;
  min-height: 34px;
  max-width: 480px;
  overflow: hidden;
  border: 1px solid ${colors.gray70};
  border-radius: 5px;
  position: relative;
`;
const Select = styled.select`
  position: absolute;
  padding-left: 15px;
  height: 100%;
  width: 100%;

  cursor: pointer;
  outline: none;
  border: none;

  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  -ms-word-break: normal;
  word-break: normal;

  background: transparent;
  z-index: 10;
`;

const Content = styled.div``;

const Option = styled.option<{ isSelected: boolean }>`
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 20px;

  ${({ isSelected }) =>
    isSelected
      ? css`
          color: ${colors.primary};
          font-weight: 600;
        `
      : css`
          color: ${colors.black};
        `}
`;

const StyledArrow = styled(Arrow)<{ isopen?: boolean }>`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%)
    ${({ isopen }) => (isopen ? ' rotate(180deg)' : ' rotate(0)')};

  path {
    fill: rgba(0, 0, 0, 1);
  }
  z-index: 1;
`;

const Description = styled.div`
  margin-top: 5px;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 20px;
  color: ${colors.grayDark};
`;
